import { useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import apiClient from '@/services/apiClient';
import {
  Card, DataTable, EmptyState, PhoneCards, PhoneScreen,
} from '@/components/kit';
import type { TableRow } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { Chip } from '@/components/kit';
import { PeriodRow } from './components/PeriodRow';
import { PhonePeriod } from './components/PhonePeriod';
import { exportRows } from './lib/exportCsv';
import {
  amount, money, monthName, periodLabel, thisPeriod, useAccountsSummary, useProfitAndLoss, useProfitByBranch,
  type ProfitAndLoss,
} from './lib/useAccounts';
import { Bar } from '../clinic-manager/components/Charts';
import { PhoneTabs } from './components/PhoneTabs';
import { AccountantMenu } from './AccountantMenu';

/**
 * Profit & loss — `g_pnl` (11). COLLECTED AGAINST SPENT.
 *
 * THE CASH VIEW, and the board's subtitle says so outright. A clinic knows
 * what landed in the bank and what left it; an accrual P&L would recognise
 * revenue on bills nobody has paid and make a month with a collection problem
 * look like its best one. The unpaid work is not lost — it is Receivables, on
 * its own screen, where it can be chased.
 *
 * A MARGIN ON NOTHING IS NOT ZERO. A month with no revenue shows no margin
 * rather than 0%, which would read as "we made nothing" instead of "nothing
 * happened".
 */
/** `2026-08` taken back by whole months. */
function shiftBack(period: string, by: number): string {
  const [y, m] = period.split('-').map(Number);
  const d = new Date(y, m - 1 - by, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function ProfitLossPage() {
  const [period, setPeriod] = useState(thisPeriod);
  const { data, isLoading, isError } = useProfitAndLoss(period);

  usePageMeta({ title: 'Profit & loss' });
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'all' | 'branch' | 'compare'>('all');
  const byBranch = useProfitByBranch(period, view === 'branch');
  const { data: summary } = useAccountsSummary(period);

  /* THE SIX MONTHS ENDING HERE — same endpoint and cache key as the month
     above, so the current bar and the headline cannot disagree. */
  const six = useQueries({
    queries: Array.from({ length: 6 }, (_, i) => shiftBack(period, 5 - i)).map((p) => ({
      queryKey: ['expenses', 'pnl', p],
      staleTime: 60_000,
      retry: false,
      queryFn: async (): Promise<ProfitAndLoss | null> => {
        const res = await apiClient.get('/expenses/pnl', { params: { period: p } });
        return ((res.data as { data?: ProfitAndLoss })?.data ?? res.data) as ProfitAndLoss;
      },
    })),
  });
  const history = six.map((q, i) => ({ period: shiftBack(period, 5 - i), profit: q.data?.profitMinor ?? 0 }));
  const histMax = Math.max(1, ...history.map((h) => Math.abs(h.profit)));
  const outs = (data?.lines ?? []).filter((l) => l.kind === 'out');
  const spendTotal = outs.reduce((n, l) => n + Math.abs(l.amountMinor), 0);

  const delta = data ? data.profitMinor - data.previousProfitMinor : 0;


  const rows: TableRow[] = [
    ...(data?.lines ?? [])
      .filter((l) => !query.trim() || l.label.toLowerCase().includes(query.trim().toLowerCase())),
    ...(data?.lines.length ? [{ label: 'Profit', amountMinor: data.profitMinor, kind: 'total' as const }] : []),
  ].map((l, i) => ({
    id: `l${i}`,
    cells: {
      line: (
        <span className={l.kind === 'in' || l.kind === 'total' ? 't-strong' : 't-body'}>
          {l.kind === 'out' ? '  ' : ''}
          {l.label}
        </span>
      ),
      amount: (
        <span className={`t-strong num${l.kind === 'total' ? '' : l.amountMinor < 0 ? ' red' : l.kind === 'in' ? ' grn' : ''}`}>
          {l.amountMinor < 0 ? '−' : ''}
          {amount(Math.abs(l.amountMinor))}
        </span>
      ),
    },
  }));

  const prev = six[4]?.data ?? null;
  const prevLabel = periodLabel(shiftBack(period, 1)).split(' ')[0];
  const thisLabel = monthName(period);
  const signed = (n: number) => `${n < 0 ? '−' : ''}${amount(Math.abs(n))}`;
  const branchRows: TableRow[] = (byBranch.data ?? []).map((b) => ({
    id: b.branchId ?? 'shared',
    cells: {
      line: <span className="t-strong">{b.name}</span>,
      rev: <span className="num grn">{amount(b.revenueMinor - b.refundsMinor)}</span>,
      spend: <span className="num red">−{amount(b.spendMinor)}</span>,
      amount: <span className={`t-strong num${b.profitMinor < 0 ? ' red' : ''}`}>{signed(b.profitMinor)}</span>,
    },
  }));
  /* COMPARE: every line this month against the month before, and the change. */
  const labels = [
    ...new Set([...(data?.lines ?? []).map((l) => l.label), ...(prev?.lines ?? []).map((l) => l.label)]),
  ];
  const compareRows: TableRow[] = [
    ...labels.map((label) => {
      const a = data?.lines.find((l) => l.label === label)?.amountMinor ?? 0;
      const b = prev?.lines.find((l) => l.label === label)?.amountMinor ?? 0;
      return [label, a, b] as const;
    }),
    ['Profit', data?.profitMinor ?? 0, prev?.profitMinor ?? 0] as const,
  ].map(([label, a, b], i) => ({
    id: `c${i}`,
    cells: {
      line: <span className={label === 'Profit' ? 't-strong' : 't-body'}>{label}</span>,
      rev: <span className="num">{signed(a)}</span>,
      spend: <span className="num mut">{signed(b)}</span>,
      amount: <span className={`t-strong num${a - b < 0 ? ' red' : a - b > 0 ? ' grn' : ''}`}>{a - b > 0 ? '+' : ''}{signed(a - b)}</span>,
    },
  }));

  useShellChrome(
    {
      raw: true,
      /* `g_pnl` — the desk table as cards, over the table's own rows, so a
         figure can never read differently here. */
      phone: (
        <PhoneScreen
          className="acc-phone"
          crumb="Accounts"
          title={`P&L · ${periodLabel(period)}`}
          menu={(close) => <AccountantMenu door="pnl" onClose={close} />}
        >
          <PhoneTabs active="pnl" />
          <PhonePeriod period={period} onChange={setPeriod} />
          {/* `g_e9` — the bottom line first, then the lines that make it. */}
          {data ? (
            <div className="card">
              <p className="t-ovl mut">{monthName(period)} · all branches</p>
              <p className={`t-dxl mt8${data.profitMinor < 0 ? ' red' : ''}`}>{money(data.profitMinor)}</p>
              <p className="t-cap mut mt4">
                profit{data.marginPercent === null ? '' : ` · ${data.marginPercent}% margin`}
              </p>
            </div>
          ) : null}
          <PhoneCards
            columns={[
              { key: 'line', label: 'Line', width: 74 },
              { key: 'amount', label: 'Amount', width: 26 },
            ]}
            rows={rows}
            body="line"
            trailing="amount"
            empty={<EmptyState title="Nothing in this month" sub="No money came in and none went out — the month has nothing to add up." />}
          />
          {summary?.insurersOweMinor ? (
            <div className="card" style={{ background: 'var(--warning-100)', borderColor: 'var(--warning-600)' }}>
              <p className="t-sec amb">
                Collected, not billed — {money(summary.insurersOweMinor)} of claims are still with insurers.
              </p>
            </div>
          ) : null}
        </PhoneScreen>
      ),
      title: 'Profit & loss',
      sub: `${monthName(period)} · all branches · cash view`,
      search: 'Search a line, a month…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        disabled: !data?.lines.length,
        onClick: () =>
          exportRows(
            `profit-and-loss-${period}`,
            ['Line', 'Amount'],
            [
              ...(data?.lines ?? []).map((l) => [
                l.label,
                (l.amountMinor / 100).toFixed(2),
              ]),
              ['Profit', ((data?.profitMinor ?? 0) / 100).toFixed(2)],
            ],
          ),
      },
    },
    [data, period, query],
  );

  return (
    <div className="maie desk acc-root">
      <PeriodRow
        period={period}
        onChange={setPeriod}
        branches={data?.branches}
        trailing={
          <>
            <span className="t-cap mut" style={{ padding: '0 4px' }}>·</span>
            <Chip kind={view === 'branch' ? 'brand' : 'outline'} small onClick={() => setView(view === 'branch' ? 'all' : 'branch')}>
              Per branch
            </Chip>
            <Chip kind={view === 'compare' ? 'brand' : 'outline'} small onClick={() => setView(view === 'compare' ? 'all' : 'compare')}>
              Compare months
            </Chip>
          </>
        }
      />

      <div className="dcontent scroll">
        <div className="pnl-grid" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: 16, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
        <Card kind="accent">
          <p className="t-ovl mut">All branches</p>
          <p className={`t-dxl${(data?.profitMinor ?? 0) < 0 ? ' red' : ''}`} style={{ margin: '4px 0' }}>
            {money(data?.profitMinor ?? 0)}
          </p>
          <p className="t-sec sec">
            {(data?.profitMinor ?? 0) < 0 ? 'loss' : 'profit'}
            {data?.marginPercent === null
              ? ' · no revenue to take a margin of'
              : ` · ${data?.marginPercent}% margin`}{' '}
            · cash view
          </p>
          {data && data.previousProfitMinor !== 0 ? (
            <p className="t-cap mut mt8">
              {delta >= 0 ? 'up' : 'down'} {amount(Math.abs(delta))} on the month before
            </p>
          ) : null}
        </Card>

        <DataTable
          caption={
            view === 'branch'
              ? `Profit by branch — ${monthName(period)}`
              : view === 'compare'
                ? `${thisLabel} against ${prevLabel}`
                : `Profit and loss — ${monthName(period)}`
          }
          columns={
            view === 'branch'
              ? [
                  { key: 'line', label: 'Branch', width: 34 },
                  { key: 'rev', label: 'Collected, net of refunds', width: 24 },
                  { key: 'spend', label: 'Spend', width: 20 },
                  { key: 'amount', label: 'Profit', width: 22 },
                ]
              : view === 'compare'
                ? [
                    { key: 'line', label: 'Line', width: 40 },
                    { key: 'rev', label: thisLabel, width: 20 },
                    { key: 'spend', label: prevLabel, width: 20 },
                    { key: 'amount', label: 'Change', width: 20 },
                  ]
                : [
                    { key: 'line', label: 'Line', width: 74 },
                    { key: 'amount', label: 'Amount', width: 26 },
                  ]
          }
          rows={view === 'branch' ? branchRows : view === 'compare' ? compareRows : rows}
          empty={
            <div className="emptystate">
              {isLoading ? (
                <p className="t-sec mut">Working out the month…</p>
              ) : isError ? (
                <p className="t-sec mut">It did not come back. Nothing has been changed.</p>
              ) : (
                <>
                  <p className="es-t">Nothing moved in {monthName(period)}</p>
                  <p className="t-sec mut">No money was collected and nothing was spent.</p>
                </>
              )}
            </div>
          }
        />
          </div>

          <div style={{ minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut">Spend, as a shape</p>
              <div className="mt8">
                {spendTotal ? (
                  [...outs]
                    .sort((x, y) => Math.abs(y.amountMinor) - Math.abs(x.amountMinor))
                    .map((l) => (
                      <Bar
                        key={l.label}
                        label={l.label}
                        value={Math.abs(l.amountMinor)}
                        max={spendTotal}
                        display={`${Math.round((Math.abs(l.amountMinor) / spendTotal) * 100)}%`}
                        tone="brand"
                      />
                    ))
                ) : (
                  <p className="t-sec mut">Nothing was spent in {monthName(period)}.</p>
                )}
              </div>
            </Card>
            <Card>
              <p className="t-ovl mut">Six months · profit</p>
              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, height: 90, marginTop: 10 }}>
                {history.map((h, i) => (
                  <div
                    key={h.period}
                    title={`${periodLabel(h.period)} · ${money(h.profit)}`}
                    style={{
                      flex: 1,
                      height: `${Math.max(6, (Math.abs(h.profit) / histMax) * 100)}%`,
                      borderRadius: 6,
                      background: i === history.length - 1 ? 'var(--brand-500)' : 'var(--brand-200)',
                    }}
                  />
                ))}
              </div>
              <p className="t-cap mut mt8">
                {periodLabel(history[0].period).split(' ')[0]} → {periodLabel(history[5].period).split(' ')[0]}
              </p>
            </Card>
            {summary?.insurersOweMinor ? (
              <Card style={{ background: 'var(--warning-100)', borderColor: 'var(--warning-600)' }}>
                <p className="t-sec amb">
                  Collected, not billed — {money(summary.insurersOweMinor)} of claims are still with insurers.
                </p>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
